"use client";

import { sizes } from "@/components/bloks/Asset";
import { GridList } from "@/components/ui/GridList";
import { LoadMoreButton } from "@/components/ui/InfiniteView/components/LoadMoreButton";
import { Sentinel } from "@/components/ui/InfiniteView/components/Sentinel";
import { useTranslations } from "@/i18n/client";
import { sbLink } from "@/lib/storyblok/link";
import { useState } from "react";
import { useArticles } from "../../ArticleListingPage/ArticlesProvider";
import { ARTICLES_PAGE_SIZE } from "../../config";
import { ArticleCard, ArticleCardSkeleton } from "../ArticleCard";
import {
	ArticleFilterbar,
	ArticleFilterbarSkeleton,
} from "../ArticleFilterbar";
import styles from "./InfiniteArticleList.module.css";

export const InfiniteArticleList = () => {
	const t = useTranslations();
	const [isAutoLoadEnabled, setIsAutoLoadEnabled] = useState<boolean>(false);

	const { featuredArticles, query } = useArticles();
	const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = query;

	return (
		<>
			<GridList
				className={styles.gridList}
				content={featuredArticles?.map((article, i) => (
					<ArticleCard
						key={i}
						{...article.content}
						sizes={sizes(100, 50)}
						previewTag={article.tag_list[0]}
						href={sbLink(article.full_slug)?.href}
						className={styles.featuredArticle}
						previewImage={article.content.previewImage}
						background={
							(article.content.previewBackground as { value?: string }).value
						}
						featuredArticle
					/>
				))}
				header={<ArticleFilterbar />}
			>
				{data?.pages.flatMap((page) =>
					page.articles.map((article, i) => (
						<ArticleCard
							key={i}
							{...article.content}
							sizes={sizes(100, 25)}
							href={sbLink(article.full_slug)?.href}
							previewImage={article.content.previewImage}
							previewTag={article.tag_list[0]}
							background={
								(article.content.previewBackground as { value?: string }).value
							}
						/>
					)),
				)}
				{isFetchingNextPage
					? [...Array(ARTICLES_PAGE_SIZE)].map((_, idx) => (
							<ArticleCardSkeleton key={idx} />
						))
					: null}
			</GridList>

			{isAutoLoadEnabled && hasNextPage ? (
				<Sentinel action={fetchNextPage} />
			) : (
				hasNextPage &&
				!isAutoLoadEnabled && (
					<LoadMoreButton
						action={() => {
							fetchNextPage();
							setIsAutoLoadEnabled(true);
						}}
						text={t("Load more articles")}
					/>
				)
			)}
		</>
	);
};

export const InfiniteArticleListSkeleton = ({
	size = ARTICLES_PAGE_SIZE,
}: {
	size?: number;
}) => {
	return (
		<GridList
			className={styles.gridList}
			content={[]}
			header={<ArticleFilterbarSkeleton />}
		>
			{[...Array(size)].map((_, idx) => (
				<ArticleCardSkeleton key={idx} />
			))}
		</GridList>
	);
};
